import {
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  AttractionFactDecisionEnum,
  AttractionFactTruthEnum,
} from "@routellers/types/dist/city-attraction/attraction-fact";
import {
  EstablishmentFactBeatEnum,
  EstablishmentFactCandidateRO,
  UpdateEstablishmentFactDto,
} from "@routellers/types/dist/city-establishment/establishment-fact";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, apiErrorMessage } from "../api";
import { Section } from "../components/editor";

type View = "all" | "pending" | "chosen" | "rejected";

const TRUTH_COLOR: Record<AttractionFactTruthEnum, string> = {
  [AttractionFactTruthEnum.UNCHECKED]: "gray",
  [AttractionFactTruthEnum.CONFIRMED]: "green",
  [AttractionFactTruthEnum.LEGEND]: "grape",
  [AttractionFactTruthEnum.DISPUTED]: "orange",
  [AttractionFactTruthEnum.UNVERIFIED]: "yellow",
  [AttractionFactTruthEnum.FALSE]: "red",
};

const DECISION_COLOR: Record<AttractionFactDecisionEnum, string> = {
  [AttractionFactDecisionEnum.PENDING]: "gray",
  [AttractionFactDecisionEnum.TAKE]: "blue",
  [AttractionFactDecisionEnum.MUST]: "teal",
  [AttractionFactDecisionEnum.REJECT]: "red",
};

/** The four moves of a pitch; find and background are not counted. */
const PITCH_BEATS = [
  EstablishmentFactBeatEnum.HOOK,
  EstablishmentFactBeatEnum.WIT,
  EstablishmentFactBeatEnum.WARMTH,
  EstablishmentFactBeatEnum.TIP,
];

function isChosen(fact: EstablishmentFactCandidateRO): boolean {
  return (
    fact.editorDecision === AttractionFactDecisionEnum.TAKE ||
    fact.editorDecision === AttractionFactDecisionEnum.MUST
  );
}

function beatOf(
  fact: EstablishmentFactCandidateRO,
): EstablishmentFactBeatEnum | null {
  return fact.editorBeat || fact.beat;
}

function ratingColor(rating: number | null): string {
  if (rating == null) return "gray";
  if (rating >= 80) return "teal";
  if (rating >= 60) return "blue";
  if (rating >= 40) return "yellow";

  return "red";
}

const FactCard = ({
  fact,
  onUpdate,
  onRemove,
}: {
  fact: EstablishmentFactCandidateRO;
  onUpdate: (
    fact: EstablishmentFactCandidateRO,
    patch: UpdateEstablishmentFactDto,
  ) => void;
  onRemove: (fact: EstablishmentFactCandidateRO) => void;
}) => {
  const { t } = useTranslation();
  const [showSources, setShowSources] = useState(false);
  const [note, setNote] = useState(fact.editorNote || "");

  useEffect(() => setNote(fact.editorNote || ""), [fact.editorNote]);

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        borderLeft: `4px solid var(--mantine-color-${DECISION_COLOR[fact.editorDecision]}-5)`,
        opacity:
          fact.editorDecision === AttractionFactDecisionEnum.REJECT ? 0.6 : 1,
      }}
    >
      <Stack gap={6}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Badge color={ratingColor(fact.aiRating)} variant="filled" miw={44}>
            {fact.aiRating ?? "—"}
          </Badge>
          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="sm" fw={600}>
              {fact.claim}
              {fact.year ? (
                <Text span size="xs" c="dimmed" fw={400}>
                  {"  "}
                  {fact.year}
                </Text>
              ) : null}
            </Text>
            {fact.detail && (
              <Text size="sm" c="dimmed">
                {fact.detail}
              </Text>
            )}
          </Stack>
          <Text size="xs" c="dimmed">
            #{fact.id}
          </Text>
        </Group>

        <Group gap={6}>
          <Badge variant="light" color={TRUTH_COLOR[fact.truthStatus]}>
            {t(`editors.attractionFacts.truth.${fact.truthStatus}`)}
          </Badge>
          {fact.beat && (
            <Badge variant="outline" color="violet">
              {t("editors.attractionFacts.aiPrefix")}{" "}
              {t(`editors.establishmentFacts.beat.${fact.beat}`)}
            </Badge>
          )}
          {fact.volatile && (
            <Badge variant="light" color="orange">
              {t("editors.attractionFacts.volatile")}
            </Badge>
          )}
          <UnstyledButton onClick={() => setShowSources((v) => !v)}>
            <Badge
              variant="dot"
              color={fact.sources.some((s) => s.quote) ? "green" : "gray"}
              style={{ cursor: "pointer" }}
            >
              {t("editors.establishmentFacts.sources", {
                count: fact.sources.length,
              })}
            </Badge>
          </UnstyledButton>
          {fact.usedIn.length > 0 && (
            <Badge variant="light" color="indigo">
              {t("editors.attractionFacts.usedIn", {
                list: fact.usedIn.join(", "),
              })}
            </Badge>
          )}
        </Group>

        {fact.aiNotes && (
          <Text size="xs" c="dimmed" fs="italic">
            {fact.aiNotes}
          </Text>
        )}

        {showSources && (
          <Stack
            gap={6}
            pl="sm"
            style={{ borderLeft: "2px solid var(--mantine-color-gray-3)" }}
          >
            {fact.sources.map((source, index) => (
              <Stack key={`${source.url}-${index}`} gap={0}>
                {source.url ? (
                  <Anchor href={source.url} target="_blank" size="xs">
                    {source.title || source.publisher || source.url}
                  </Anchor>
                ) : (
                  <Text size="xs">{source.title || source.publisher}</Text>
                )}
                {source.quote && (
                  <Text size="xs" c="dimmed">
                    «{source.quote}»
                  </Text>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        <Group gap="xs" align="flex-end">
          <SegmentedControl
            size="xs"
            value={fact.editorDecision}
            color={DECISION_COLOR[fact.editorDecision]}
            onChange={(value) =>
              onUpdate(fact, {
                editorDecision: value as AttractionFactDecisionEnum,
              })
            }
            data={Object.values(AttractionFactDecisionEnum).map((value) => ({
              value,
              label: t(`editors.attractionFacts.decision.${value}`),
            }))}
          />
          <Select
            size="xs"
            w={170}
            clearable
            placeholder={t("editors.establishmentFacts.beatPlaceholder")}
            value={fact.editorBeat}
            onChange={(value) =>
              onUpdate(fact, {
                editorBeat: (value as EstablishmentFactBeatEnum) || null,
              })
            }
            data={Object.values(EstablishmentFactBeatEnum).map((value) => ({
              value,
              label: t(`editors.establishmentFacts.beat.${value}`),
            }))}
          />
          <TextInput
            size="xs"
            style={{ flex: 1, minWidth: 180 }}
            placeholder={t("editors.attractionFacts.notePlaceholder")}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
            onBlur={() => {
              if (note !== (fact.editorNote || ""))
                onUpdate(fact, { editorNote: note });
            }}
          />
          <Button
            size="xs"
            variant="subtle"
            color="red"
            onClick={() => onRemove(fact)}
          >
            {t("editors.establishmentFacts.remove")}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

const NewFactForm = ({
  establishmentId,
  onCreated,
}: {
  establishmentId: number;
  onCreated: () => void;
}) => {
  const { t } = useTranslation();
  const [claim, setClaim] = useState("");
  const [detail, setDetail] = useState("");
  const [beat, setBeat] = useState<EstablishmentFactBeatEnum | null>(null);
  const [url, setUrl] = useState("");
  const [quote, setQuote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.cityEstablishmentsApi.createFact({
        id: establishmentId,
        data: {
          claim,
          detail: detail || null,
          beat,
          sources:
            url || quote ? [{ url: url || null, quote: quote || null }] : [],
        },
      });
      setClaim("");
      setDetail("");
      setBeat(null);
      setUrl("");
      setQuote("");
      onCreated();
    } catch (error) {
      notifications.show({ color: "red", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Textarea
          autosize
          minRows={1}
          label={t("editors.attractionFacts.newClaim")}
          value={claim}
          onChange={(event) => setClaim(event.currentTarget.value)}
        />
        <Group grow align="flex-start">
          <Textarea
            autosize
            minRows={1}
            label={t("editors.attractionFacts.newDetail")}
            value={detail}
            onChange={(event) => setDetail(event.currentTarget.value)}
          />
          <Select
            clearable
            label={t("editors.establishmentFacts.beatPlaceholder")}
            value={beat}
            onChange={(value) =>
              setBeat((value as EstablishmentFactBeatEnum) || null)
            }
            data={Object.values(EstablishmentFactBeatEnum).map((value) => ({
              value,
              label: t(`editors.establishmentFacts.beat.${value}`),
            }))}
          />
        </Group>
        <Group grow>
          <TextInput
            label={t("editors.attractionFacts.newUrl")}
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
          />
          <TextInput
            label={t("editors.attractionFacts.newQuote")}
            value={quote}
            onChange={(event) => setQuote(event.currentTarget.value)}
          />
        </Group>
        <Group justify="flex-end">
          <Button
            size="xs"
            loading={saving}
            disabled={!claim.trim()}
            onClick={submit}
          >
            {t("editors.attractionFacts.addFact")}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

/** Fact bank of a venue: the pitch is written only from take/must facts. */
export const EstablishmentFactsPanel = ({
  establishmentId,
  title,
}: {
  establishmentId: number;
  title: string;
}) => {
  const { t } = useTranslation();
  const [facts, setFacts] = useState<EstablishmentFactCandidateRO[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("all");
  const [beatFilter, setBeatFilter] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.cityEstablishmentsApi.listFacts(establishmentId);
    setFacts(data);
  }, [establishmentId]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((error) =>
        notifications.show({ color: "red", message: apiErrorMessage(error) }),
      )
      .finally(() => setLoading(false));
  }, [load]);

  const update = async (
    fact: EstablishmentFactCandidateRO,
    patch: UpdateEstablishmentFactDto,
  ) => {
    try {
      const { data } = await api.cityEstablishmentsApi.updateFact({
        factId: fact.id,
        data: patch,
      });
      setFacts((list) =>
        list.map((item) => (item.id === data.id ? data : item)),
      );
    } catch (error) {
      notifications.show({ color: "red", message: apiErrorMessage(error) });
    }
  };

  const remove = async (fact: EstablishmentFactCandidateRO) => {
    if (!window.confirm(t("editors.establishmentFacts.removeConfirm"))) return;
    try {
      await api.cityEstablishmentsApi.removeFact(fact.id);
      setFacts((list) => list.filter((item) => item.id !== fact.id));
    } catch (error) {
      notifications.show({ color: "red", message: apiErrorMessage(error) });
    }
  };

  const chosen = useMemo(() => facts.filter(isChosen), [facts]);

  const coverage = useMemo(
    () =>
      PITCH_BEATS.map((beat) => ({
        beat,
        count: chosen.filter((fact) => beatOf(fact) === beat).length,
      })),
    [chosen],
  );

  const visible = useMemo(
    () =>
      facts.filter((fact) => {
        if (view === "chosen" && !isChosen(fact)) return false;
        if (
          view === "pending" &&
          fact.editorDecision !== AttractionFactDecisionEnum.PENDING
        )
          return false;
        if (
          view === "rejected" &&
          fact.editorDecision !== AttractionFactDecisionEnum.REJECT
        )
          return false;
        if (beatFilter && beatOf(fact) !== beatFilter) return false;

        return true;
      }),
    [facts, view, beatFilter],
  );

  const copyPack = async () => {
    const lines = [
      `# ${title}`,
      "",
      t("editors.establishmentFacts.packIntro"),
      "",
    ];
    for (const beat of Object.values(EstablishmentFactBeatEnum)) {
      const group = chosen.filter(
        (fact) => (beatOf(fact) || "background") === beat,
      );
      if (!group.length) continue;
      lines.push(`## ${t(`editors.establishmentFacts.beat.${beat}`)}`);
      for (const fact of group) {
        const legend =
          fact.truthStatus === AttractionFactTruthEnum.LEGEND ? " (байка)" : "";
        const must =
          fact.editorDecision === AttractionFactDecisionEnum.MUST ? " [!]" : "";
        lines.push(`- #${fact.id}${must}${legend} ${fact.claim}`);
        if (fact.detail) lines.push(`  ${fact.detail}`);
        if (fact.editorNote) lines.push(`  Редактор: ${fact.editorNote}`);
      }
      lines.push("");
    }
    await navigator.clipboard.writeText(lines.join("\n"));
    notifications.show({
      color: "green",
      message: t("editors.establishmentFacts.packCopied"),
    });
  };

  return (
    <Stack gap="md">
      <Section
        title={t("editors.establishmentFacts.title")}
        description={t("editors.establishmentFacts.description")}
        boxed
      >
        <Group gap="xs">
          {coverage.map(({ beat, count }) => (
            <Badge key={beat} variant="light" color={count ? "teal" : "red"}>
              {t(`editors.establishmentFacts.beat.${beat}`)}: {count}
            </Badge>
          ))}
          <Button
            size="xs"
            variant="light"
            ml="auto"
            disabled={!chosen.length}
            onClick={() => void copyPack()}
          >
            {t("editors.establishmentFacts.copyPack")}
          </Button>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => setAdding((v) => !v)}
          >
            {t("editors.attractionFacts.addFact")}
          </Button>
        </Group>
      </Section>

      {adding && (
        <NewFactForm
          establishmentId={establishmentId}
          onCreated={() => {
            setAdding(false);
            void load();
          }}
        />
      )}

      <Group gap="xs">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(value) => setView(value as View)}
          data={(["all", "pending", "chosen", "rejected"] as View[]).map(
            (value) => ({
              value,
              label: t(`editors.attractionFacts.view.${value}`),
            }),
          )}
        />
        <Select
          size="xs"
          w={190}
          clearable
          placeholder={t("editors.establishmentFacts.beatPlaceholder")}
          value={beatFilter}
          onChange={(value) => setBeatFilter(value ? String(value) : null)}
          data={Object.values(EstablishmentFactBeatEnum).map((value) => ({
            value,
            label: t(`editors.establishmentFacts.beat.${value}`),
          }))}
        />
        <Text size="xs" c="dimmed">
          {t("editors.establishmentFacts.counter", {
            shown: visible.length,
            total: facts.length,
            chosen: chosen.length,
          })}
        </Text>
      </Group>

      {loading ? (
        <Text size="sm" c="dimmed">
          {t("editors.attractionFacts.loading")}
        </Text>
      ) : visible.length ? (
        visible.map((fact) => (
          <FactCard
            key={fact.id}
            fact={fact}
            onUpdate={update}
            onRemove={remove}
          />
        ))
      ) : (
        <Text size="sm" c="dimmed">
          {facts.length
            ? t("editors.attractionFacts.empty")
            : t("editors.establishmentFacts.emptyBank")}
        </Text>
      )}
    </Stack>
  );
};
